import { useCallback, useContext, useEffect, useState } from 'react';
import { CONVERSATION_CONSTANTS, STORAGE_KEYS } from '../../config/miaw/constants';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { IApiHooksWithTriggerData, IIdentityObject } from '../../interfaces/app-interfaces';
import { createConversation, getUnauthenticatedAccessToken, listConversationEntries } from '../../utils/miaw/api-utils';
import { getConversationId, setDeploymentConfiguration, setJwt, setLastEventId, storeConversationId } from '../../utils/miaw/chat-config';
import { util } from '../../utils/miaw/common';
import { setItemInWebStorage } from '../../utils/miaw/web-storage-utils';
import { IStartChatData } from '../../interfaces/api-interfaces-miaw';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { CAContext } from '../../contexts/CAContext';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { useMIAWAgentActions } from '../miaw/useMIAWAgentActions';
import { getRoutingAttributes } from '../../utils/miaw/start-chat-utils';
import { CaseDataContext } from '../../contexts/CaseDataContext';
import { getQueue } from '../../utils/miaw/init-chat-utils';
import { useIdentity } from '@vp/auth-react';
import { setIsUserLoggedInAtChatStart, setPreChatInfoLocalStorage } from '../../utils/session-storage-utils';
import { useCBChatEscalate } from '../chatbot/useCBEscalate';
import { IRoutingResultPayload } from '../../interfaces/agent-action-interfaces';

const useMIAWStartChat = (): IApiHooksWithTriggerData<string, IStartChatData> => {
  const { trigger: triggerEscalate } = useCBChatEscalate();
  const { logCustomAttributesNR } = useNRLogging();
  const identity = useIdentity();

  const {
    chatState: { userInfo, customQueue },
  } = useContext(CAContext);
  const {
    state: { conversationStatus, conversationId, queueInfo, studioDesignLink, sfConfig },
    setState,
  } = useContext(MiawChatContext);
  const {
    state: { orderNumber, selectedProductIds, sessionId },
  } = useContext(ChatbotContext);

  const {
    state: { caseData, queueType },
  } = useContext(CaseDataContext);

  const { subscribeToAgentActions } = useMIAWAgentActions();
  const [data, setData] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [requestData, setRequestData] = useState<IStartChatData>();

  const trigger = useCallback((chatDetails: IStartChatData) => {
    /** Set pre-chat information in local storage for using after page change on session restart */
    setPreChatInfoLocalStorage({
      firstName: chatDetails.firstName,
      lastName: chatDetails.lastName,
      email: chatDetails.email,
    });
    console.log('Triggering start chat with details:', chatDetails);
    if (chatDetails?.chatbotIdentifier) {
      triggerEscalate();
    }
    setRequestData(chatDetails);
  }, []);

  const errorLog = useCallback((err: Error) => {
    logCustomAttributesNR('ca-chatbot-miaw-api-error', {
      errorName: err.name,
      errorStack: err.stack,
      message: err.message,
      logLevel: 'error',
      apiType: 'miawStartConversation',
    });
  }, []);

  useEffect(() => {
    if (requestData) {
      setLoading(true);
      logCustomAttributesNR('ca-chatbot-miaw-api-request', {
        logLevel: 'info',
        message: 'Starting new conversation',
        apiType: 'miawStartConversation',
      });
      getUnauthenticatedAccessToken(sfConfig)
        .then((response) => {
          console.log('Successfully fetched an Unauthenticated access token.');
          // Parse the response object which includes access-token (JWT), configutation data.
          if (response && typeof response === 'object' && response.accessToken) {
            setJwt(response.accessToken);
            setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
            setLastEventId(response.lastEventId);
            setDeploymentConfiguration(response.context && response.context.configuration && response.context.configuration.embeddedServiceConfig);
          }
        })
        .then(async () => {
          if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            console.warn('Cannot create a new conversation while a conversation is currently open.');

            return Promise.reject();
          }

          // Initialize a new unique conversation-id in-memory.
          storeConversationId(util.generateUUID());
          try {
            if (caseData) {
              if (!conversationId) {
                const { canonicalId } = identity as IIdentityObject;

                logCustomAttributesNR('ca-chatbot-miaw-api-request', {
                  logLevel: 'info',
                  message: `Logging user email exists: ${userInfo?.userData?.email ? 'yes' : 'no'}  and lastName exists: ${userInfo?.userData?.lastName ? 'yes' : 'no'}`,
                  conversationId,
                  apiType: 'miawStartConversation',
                });
                setState((prev) => ({ ...prev, conversationId: getConversationId() }));
                if (userInfo?.userData?.email && userInfo?.userData?.lastName) {
                  await createConversation(
                    getConversationId(),
                    {
                      ...getRoutingAttributes(
                        {
                          chatbotIdentifier: sessionId,
                          orderNumber,
                          selectedProductIds,
                          userShopperId: canonicalId,
                          chatbotContactReason: requestData.chatbotContactReason || '',
                          studioDesignLink,
                          isLoggedIn: userInfo?.isLoggedIn?.toString() || '',
                        },
                        caseData, // Pass caseData to getRoutingAttributes
                        getQueue(customQueue || queueType, queueInfo),
                        customQueue || queueType,
                        userInfo?.userData,
                      ),
                    },
                    sfConfig,
                  )
                    .then(() => {
                      console.log(`Successfully created a new conversation with conversation-id: ${getConversationId()}`);
                      if (getConversationId() !== undefined) {
                        listConversationEntries(getConversationId() as string)
                          .then((response) => {
                            response.forEach((item) => {
                              const entry = item?.conversationEntry;
                              if (entry?.entryType === 'RoutingResult') {
                                const payload = entry.entryPayload as IRoutingResultPayload;
                                const failureType = payload?.failureType;
                                const failureReason = payload?.failureReason;
                                if (failureType === 'RoutingError' && failureReason && failureReason !== '') {
                                  logCustomAttributesNR('ca-chatbot-miaw-start-chat', {
                                    logLevel: 'error',
                                    message: `Agents not available`,
                                    conversationId: getConversationId(),
                                    apiType: 'miawStartConversation',
                                  });
                                  setState((prev) => {
                                    return {
                                      ...prev,
                                      conversationMessages: [
                                        ...prev.conversationMessages,
                                        {
                                          id: entry.identifier,
                                          messageContent:
                                            failureReason ??
                                            'Sorry, our specialists aren’t available right now. Please try again later or contact us by email or phone instead.',
                                          senderDisplayName: entry.senderDisplayName,
                                          timestamp: entry.clientTimestamp,
                                          type: 'system',
                                          showFeedback: false,
                                        },
                                      ],
                                      isRoutingError: true,
                                    };
                                  });
                                }
                              }
                            });
                          })
                          .catch((ex) => {
                            setError(ex as Error);
                            errorLog(ex as Error);
                          });
                      }
                      setState((prev) => ({
                        ...prev,
                        conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION,
                      }));
                      const { isSignedIn } = identity as IIdentityObject;
                      setIsUserLoggedInAtChatStart(isSignedIn);
                    })
                    .then(() => {
                      console.log(`Completed initializing a new conversation with conversationId: ${getConversationId()}`);
                      setData(`Conversation created successfully id ${getConversationId()}`);
                      setError(undefined);
                      subscribeToAgentActions();
                      setLoading(false);
                      logCustomAttributesNR('ca-chatbot-miaw-api-response', {
                        logLevel: 'info',
                        message: 'Conversation created successfully',
                        conversationId: getConversationId(),
                        apiType: 'miawStartConversation',
                      });
                    })
                    .catch((err) => {
                      console.error({ err });
                      if (err instanceof Response) {
                        err
                          .text()
                          .then((errorMessage: string) => {
                            errorLog(new Error(errorMessage));
                          })
                          .catch((textError) => {
                            console.error(textError);
                          });
                      } else {
                        errorLog(err as Error);
                      }
                    });
                } else {
                  console.error('User information is incomplete. Cannot start conversation.');
                  throw new Error('User information is incomplete. Cannot start conversation.');
                }
              }
            }
          } catch (err) {
            console.error(`Something went wrong in creating a new conversation with conversation-id: ${getConversationId()}: ${err}`);
            if (err instanceof Response) {
              err
                .text()
                .then((errorMessage: string) => {
                  errorLog(new Error(errorMessage));
                })
                .catch((textError) => {
                  console.error(textError);
                });
            } else {
              errorLog(err as Error);
            }
            throw new Error('Failed to create a new conversation.');
          }
        })
        .catch((err) => {
          console.error(err);
          setError(err as Error);
          if (err instanceof Response) {
            err
              .text()
              .then((errorMessage: string) => {
                errorLog(new Error(errorMessage));
              })
              .catch((textError) => {
                console.error(textError);
              });
          } else {
            errorLog(err as Error);
          }
        })
        .finally(() => {
          setLoading(false);
          setRequestData(undefined); // Reset requestData after completion
        });
    }
  }, [requestData]);

  return {
    loading,
    trigger,
    data,
    error,
  };
};

export { useMIAWStartChat };
